/* eslint-disable no-restricted-syntax */
import { useEffect, useRef, useCallback, useMemo, useState } from 'react';

const DRAFT_PREFIX = 'expertise_draft_';
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000; // 1 day
const MAX_DRAFTS = 10;
const DEBOUNCE_MS = 3000;
const CLEANUP_THROTTLE_MS = 60000; // Only cleanup every 60 seconds
const SETTLE_DELAY_MS = 500; // Wait for form to settle before tracking changes

interface DraftData<T> {
  formValues: T;
  timestamp: number;
  customerId: number;
  reportType: string;
  version?: number;
}

interface UseAutoSaveReturn<T> {
  clearDraft: () => void;
  pendingDraft: { values: T; date: Date } | null;
  recoverDraft: () => void;
  dismissDraft: () => void;
}

// Module-level timestamp for throttling cleanup
let lastCleanupTime = 0;

// Type guard to validate recovered data structure
function isValidDraftData<T>(data: unknown): data is DraftData<T> {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    'formValues' in d &&
    typeof d.timestamp === 'number' &&
    typeof d.customerId === 'number' &&
    typeof d.reportType === 'string'
  );
}

/**
 * Compares two objects and returns only the changed fields (delta).
 * Uses shallow comparison for primitives and recursive comparison for nested objects.
 * Returns null if no changes detected.
 */
function getChangedFields<T extends Record<string, unknown>>(current: T, initial: T): Record<string, unknown> | null {
  const changed: Record<string, unknown> = {};
  let hasChanges = false;

  // Combine keys from both objects
  const currentKeys = Object.keys(current);
  const initialKeys = Object.keys(initial);
  const allKeys = currentKeys.concat(initialKeys.filter((k) => !currentKeys.includes(k)));

  for (const key of allKeys) {
    const currVal = current[key];
    const initVal = initial[key];

    // Handle undefined/null cases
    if (currVal === initVal) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // Nested object comparison (for 'answers' object)
    if (
      typeof currVal === 'object' &&
      currVal !== null &&
      typeof initVal === 'object' &&
      initVal !== null &&
      !Array.isArray(currVal)
    ) {
      const nestedChanges = getChangedFields(currVal as Record<string, unknown>, initVal as Record<string, unknown>);
      if (nestedChanges) {
        changed[key] = nestedChanges;
        hasChanges = true;
      }
    }
    // Primitive comparison
    else if (currVal !== initVal) {
      changed[key] = currVal;
      hasChanges = true;
    }
  }

  return hasChanges ? changed : null;
}

/**
 * Merges delta changes back into initial values to reconstruct full form values.
 */
function applyDelta<T extends Record<string, unknown>>(initial: T, delta: Record<string, unknown>): T {
  const result = { ...initial } as Record<string, unknown>;

  const deltaKeys = Object.keys(delta);
  for (const key of deltaKeys) {
    const deltaVal = delta[key];
    const initVal = initial[key];

    // Nested object merge
    if (
      typeof deltaVal === 'object' &&
      deltaVal !== null &&
      typeof initVal === 'object' &&
      initVal !== null &&
      !Array.isArray(deltaVal)
    ) {
      result[key] = applyDelta(initVal as Record<string, unknown>, deltaVal as Record<string, unknown>);
    } else {
      result[key] = deltaVal;
    }
  }

  return result as T;
}

function cleanupOldDrafts() {
  const now = Date.now();
  const drafts: { key: string; timestamp: number }[] = [];
  const keysToRemove: string[] = [];

  // First pass: collect all draft keys (safe iteration)
  const allKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(DRAFT_PREFIX)) {
      allKeys.push(key);
    }
  }

  // Second pass: analyze each draft
  allKeys.forEach((key) => {
    try {
      const data = JSON.parse(localStorage.getItem(key) || '{}');
      if (now - data.timestamp > MAX_DRAFT_AGE_MS) {
        keysToRemove.push(key);
      } else {
        drafts.push({ key, timestamp: data.timestamp });
      }
    } catch {
      keysToRemove.push(key);
    }
  });

  // Remove expired/corrupted drafts
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Keep only MAX_DRAFTS (remove oldest)
  if (drafts.length > MAX_DRAFTS) {
    drafts.sort((a, b) => b.timestamp - a.timestamp);
    drafts.slice(MAX_DRAFTS).forEach((d) => localStorage.removeItem(d.key));
  }
}

export function useAutoSave<T extends Record<string, unknown>>(
  customerId: number | undefined,
  reportType: string,
  formValues: T,
  getInitialValues: () => T,
  onRecover: (values: T) => void,
  enabled: boolean = true,
): UseAutoSaveReturn<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasCheckedRef = useRef(false);
  const isSettledRef = useRef(false);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Pending draft state - shown to user for confirmation
  const [pendingDraft, setPendingDraft] = useState<{ values: T; date: Date } | null>(null);

  // Store callbacks in refs to avoid stale closures
  const onRecoverRef = useRef(onRecover);
  onRecoverRef.current = onRecover;

  // CRITICAL: Freeze initial values once when form becomes enabled
  // This prevents API data from being saved as "changes"
  const frozenInitialRef = useRef<T | null>(null);
  const isInitializedRef = useRef(false);

  // Memoize draftKey to avoid recalculation
  const draftKey = useMemo(
    () => (customerId ? `${DRAFT_PREFIX}${customerId}_${reportType}` : ''),
    [customerId, reportType],
  );

  // Track previous delta to avoid unnecessary saves
  const prevDeltaRef = useRef<string>('');
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  // Initialize frozen initial values when enabled
  useEffect(() => {
    if (enabled && !isInitializedRef.current && customerId) {
      // Freeze initial values from the getter function
      frozenInitialRef.current = getInitialValues();
      isInitializedRef.current = true;

      // Start settle timer
      isSettledRef.current = false;
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
      settleTimeoutRef.current = setTimeout(() => {
        isSettledRef.current = true;
      }, SETTLE_DELAY_MS);
    }

    // Reset when disabled (drawer closes)
    if (!enabled) {
      hasCheckedRef.current = false;
      isInitializedRef.current = false;
      isSettledRef.current = false;
      frozenInitialRef.current = null;
      prevDeltaRef.current = '';
      setPendingDraft(null);

      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
    }

    return () => {
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
    };
  }, [enabled, customerId, getInitialValues]);

  // Clear draft function
  const clearDraft = useCallback(() => {
    if (draftKey) {
      localStorage.removeItem(draftKey);
      setPendingDraft(null);
      prevDeltaRef.current = '';
    }
  }, [draftKey]);

  // User confirms recovery
  const recoverDraft = useCallback(() => {
    if (pendingDraft) {
      onRecoverRef.current(pendingDraft.values);
      setPendingDraft(null);
    }
  }, [pendingDraft]);

  // User dismisses draft
  const dismissDraft = useCallback(() => {
    clearDraft();
  }, [clearDraft]);

  // Delta-based save effect - only saves changed fields
  useEffect(() => {
    if (!enabled || !customerId || !draftKey) {
      return undefined;
    }

    // Don't save until form is settled (prevents saving initial API data)
    if (!isSettledRef.current || !frozenInitialRef.current) {
      return undefined;
    }

    // Get changed fields using field-level comparison (not JSON.stringify)
    const changedFields = getChangedFields(formValues, frozenInitialRef.current);

    // No changes - clear any existing draft
    if (!changedFields) {
      if (prevDeltaRef.current) {
        localStorage.removeItem(draftKey);
        prevDeltaRef.current = '';
      }
      return undefined;
    }

    // Serialize only the delta (much smaller than full form)
    const serializedDelta = JSON.stringify(changedFields);

    // Skip if delta hasn't changed
    if (serializedDelta === prevDeltaRef.current) {
      return undefined;
    }
    prevDeltaRef.current = serializedDelta;

    // Clear any pending save
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      try {
        // Reconstruct full form values from initial + delta
        const fullValues = applyDelta(frozenInitialRef.current!, changedFields);

        // Build draft data
        const draftData: DraftData<T> = {
          formValues: fullValues,
          timestamp: Date.now(),
          customerId,
          reportType,
          version: 2,
        };
        localStorage.setItem(draftKey, JSON.stringify(draftData));

        // Throttled cleanup
        const now = Date.now();
        if (now - lastCleanupTime > CLEANUP_THROTTLE_MS) {
          lastCleanupTime = now;
          // Run cleanup async to not block
          setTimeout(cleanupOldDrafts, 0);
        }
      } catch (e) {
        console.warn('Auto-save failed:', e);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formValues, draftKey, customerId, reportType, enabled]);

  // Check for draft on mount - sets pendingDraft for user confirmation
  useEffect(() => {
    if (!enabled || !customerId || !draftKey) return;
    if (hasCheckedRef.current) return;

    hasCheckedRef.current = true;

    try {
      const draft = localStorage.getItem(draftKey);
      if (!draft) return;

      const parsed = JSON.parse(draft);

      if (!isValidDraftData<T>(parsed)) {
        localStorage.removeItem(draftKey);
        return;
      }

      const age = Date.now() - parsed.timestamp;

      if (age < MAX_DRAFT_AGE_MS) {
        // Get current initial values for comparison
        const currentInitial = getInitialValues();

        // Compare draft with initial values - don't show if draft equals initial
        const draftDelta = getChangedFields(parsed.formValues, currentInitial);
        if (!draftDelta) {
          localStorage.removeItem(draftKey);
          return;
        }

        // Draft has real changes, show recovery option
        setPendingDraft({
          values: parsed.formValues,
          date: new Date(parsed.timestamp),
        });
      } else {
        localStorage.removeItem(draftKey);
      }
    } catch {
      localStorage.removeItem(draftKey);
    }
  }, [customerId, draftKey, enabled, getInitialValues]);

  return { clearDraft, pendingDraft, recoverDraft, dismissDraft };
}
